import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio } from "lucide-react";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal } from "@/components/shared/Reveal";
import { useMarketplaceMarkers } from "@/hooks/useMarketplaceMarkers";
import { KIND_META, FILTERS, US_MARKERS, WORLD_MARKERS } from "@/components/sections/marketplaceMapConstants";
import { MarketplaceMapCanvas } from "@/components/sections/MarketplaceMapCanvas";
import { MarketplaceFilterBar } from "@/components/sections/MarketplaceFilterBar";

export const MarketplaceMap = () => {
  const [zoom, setZoom] = useState("us");
  const [activeFilters, setActiveFilters] = useState(new Set(FILTERS));
  const { live, liveMarkers, usingLive } = useMarketplaceMarkers();

  const toggleFilter = (f) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(f)) {
        if (next.size > 1) next.delete(f);
      } else {
        next.add(f);
      }
      return next;
    });
  };

  const sourceMarkers = usingLive ? liveMarkers : zoom === "us" ? US_MARKERS : WORLD_MARKERS;
  const markers = sourceMarkers.filter((m) => activeFilters.has(KIND_META[m.kind].filter));

  return (
    <section id="map" data-testid="marketplace-map-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Marketplace Preview"
          title="Transportation Opportunities Across the Map"
          testId="marketplace-map"
        />

        <Reveal delay={0.1}>
          <div data-testid="marketplace-live-status" className="mt-6 inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full bg-rr-surface border border-rr-line text-rr-navy">
            <Radio size={13} className={live?.connected ? "text-rr-green" : "text-rr-muted"} />
            {live?.connected
              ? `Live from RelayRigs App — ${live.counts.loads} Loads · ${live.counts.relief} Relief · ${live.counts.rv_rescue + live.counts.rv_delivery} RV`
              : "Connecting to live marketplace…"}
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <MarketplaceFilterBar
            filters={FILTERS}
            activeFilters={activeFilters}
            onToggleFilter={toggleFilter}
            zoom={zoom}
            onZoomChange={setZoom}
          />
        </Reveal>

        <Reveal delay={0.3}>
          <MarketplaceMapCanvas zoom={zoom} markers={markers} usingLive={usingLive} />
        </Reveal>

        <Reveal delay={0.4}>
          <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
            <p className="text-sm text-rr-muted max-w-lg">
              Public visitors see approximate marketplace activity rather than sensitive exact vehicle or driver locations.
            </p>
            <Link to="/opportunities" data-testid="explore-opportunities-cta" className="inline-flex items-center gap-2 font-bold text-rr-navy hover:text-rr-orange transition-colors">
              Explore Opportunities <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
